import { useTheme } from '@mui/styles';
import {
    Deposit, HandshakeIcon, Redeem, Send, Transact, Withdrawal
} from '@tassat/icon';
import { muiTheme } from '@tassat/payments-themes';
import * as React from 'react';

import { buttonStyles, iconStyles } from './styles';
import { SubmitButtonIconProps, SubmitButtonProps } from './types';

function SubmitButtonIcon(props: SubmitButtonIconProps) {
    const { type } = props;

    const classes = iconStyles(props);
    const theme = useTheme<typeof muiTheme>();

    switch (type) {
        case 'send':
        case 'send-token':
        case 'send-confirm':
        case 'send-approve':
        case 'send-ach':
            return (
                <Send
                    className={classes.buttonIcon}
                    iconColor={theme?.app?.applicationColors.enumColors.white || '#FFF'}
                />
            );
        case 'redemption-token':
        case 'subscription-token':
        case 'buy-token':
        case 'sell-token':
        case 'redemption-confirm':
        case 'subscription-confirm':
        case 'buy-confirm':
        case 'sell-confirm':
            return (
                <Transact
                    className={classes.buttonIcon}
                    iconColor={theme?.app?.applicationColors.enumColors.white || '#FFF'}
                />
            );
        case 'redeem':
        case 'redeem-token':
        case 'adjustment':
        case 'adjustment-token':
        case 'redeem-confirm':
        case 'adjustment-confirm':
        case 'redeem-approve':
        case 'adjustment-approve':
            return (
                <Redeem
                    className={classes.buttonIcon}
                    iconColor={theme?.app?.applicationColors.enumColors.white || '#FFF'}
                />
            );
        case 'deposit':
        case 'deposit-confirm':
        case 'deposit-approve':
            return (
                <Deposit
                    className={classes.buttonIcon}
                    iconColor={theme?.app?.applicationColors.enumColors.white || '#FFF'}
                />
            );
        case 'withdraw':
        case 'withdraw-approve':
        case 'withdraw-confirm':
            return (
                <Withdrawal
                    className={classes.buttonIcon}
                    iconColor={theme?.app?.applicationColors.enumColors.white || '#FFF'}
                />
            );
        case 'pledgeCollateral':
        case 'pledgeCollateral-approve':
        case 'pledgeCollateral-confirm':
            return (
                <HandshakeIcon
                    className={classes.buttonIcon}
                    iconColor={theme?.app?.applicationColors.enumColors.white || '#FFF'}
                />
            );
        default:
            return null;
    }
}

function renderTextFromType(type, tokenName) {
    switch (type) {
        case 'send':
            return 'Send';
        case 'send-token':
            return `Send ${tokenName}`;
        case 'send-confirm':
            return 'Confirm Send';
        case 'send-approve':
            return 'Approve Send';
        case 'send-ach':
            return 'Send ACH';
        case 'redeem':
        case 'adjustment':
            return 'Redeem';
        case 'redeem-token':
        case 'adjustment-token':
            return `Redeem ${tokenName}`;
        case 'subscription-token':
            return 'Subscribe';
        case 'redemption-token':
            return 'Redeem';
        case 'buy-token':
            return 'Buy';
        case 'buy-confirm':
        case 'sell-confirm':
        case 'redemption-confirm':
        case 'subscription-confirm':
            return 'Confirm';
        case 'sell-token':
            return 'Sell';
        case 'redeem-confirm':
        case 'adjustment-confirm':
            return 'Confirm Redeem';
        case 'redeem-approve':
        case 'adjustment-approve':
            return 'Approve Redeem';
        case 'deposit':
            return 'Deposit Funds';
        case 'deposit-confirm':
            return 'Confirm Deposit';
        case 'deposit-approve':
            return 'Approve Deposit';
        case 'save':
            return 'Save Changes';
        case 'withdraw-approve':
            return 'Approve';
        case 'withdraw-confirm':
            return 'Confirm';
        case 'pledgeCollateral-approve':
            return 'Approve';
        case 'pledgeCollateral-confirm':
            return 'Confirm';
        default:
            return type;
    }
}

export default function SubmitButton(props: SubmitButtonProps) {
    const {
        className,
        type,
        tokenName,
        disabled,
        text,
        backgroundColor,
        sizing,
        onClick
    } = props;

    const classes = buttonStyles(props);
    const colorStyles = React.useMemo(() => {
        if (backgroundColor === 'approve') {
            return classes.approveColor;
        }

        if (backgroundColor === 'decline' || type === 'decline') {
            return classes.declineColor;
        }

        return classes.primaryColor;
    }, [
        classes,
        backgroundColor,
        type
    ]);

    const sizeStyles = React.useMemo(() => {
        if (sizing === 'tight') {
            return classes.tightStyle;
        }

        return classes.defaultStyle;
    }, [
        classes,
        sizing
    ]);

    return (
        <button
            className={`${className || ''} ${classes.submitButtonStyle} ${sizeStyles} ${colorStyles}`}
            disabled={disabled}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            type='button'
        >
            <SubmitButtonIcon type={type} />
            {text || renderTextFromType(type, tokenName)}
        </button>
    );
}